import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EllipsisVerticalIcon,
  GripVerticalIcon,
  PlusIcon,
  SortableList,
  Table,
  TableCell,
} from '@fukuri-admins/ui/common';
import { iconButtonVariants } from '@fukuri-admins/ui/common/IconButton/logic';
import { PortalIcon } from './PortalIcon';
import styles from './styles.module.scss';

/** Table のヘッダーが受け取るヘルプオブジェクトの型（content はこのファイルで組み立てる） */
type HeaderHelpContent = NonNullable<
  React.ComponentProps<typeof Table>['headers'][number]['helpContent']
>;

type Portal = {
  id: number;
  iconName: React.ComponentProps<typeof PortalIcon>['iconName'];
  title: string;
  linkUrl?: string;
  fileUrl?: string;
  itemType: 'file' | 'url';
  sortOrder: number;
};

type Props = {
  portalItems: Portal[];
  onReOrder: (items: Portal[]) => void;
  handleEditPortal: (portal: Portal) => void;
  handleDeletePortal: (portal: Portal) => void;
  handleOpenCreateModal: () => void;
  /** 「並び順」ヘルプアイコンのツールチップ表示ハンドラー */
  showTooltip: HeaderHelpContent['showHelp'];
  /** ツールチップを非表示にするハンドラー */
  hideTooltip: HeaderHelpContent['hideHelp'];
  /** ツールチップの id */
  tooltipId: HeaderHelpContent['helpId'];
};

export const PortalListPresentation: React.FC<Props> = ({
  portalItems,
  onReOrder,
  handleEditPortal,
  handleDeletePortal,
  handleOpenCreateModal,
  showTooltip,
  hideTooltip,
  tooltipId,
}) => (
  <div className={styles.container}>
    <div className={styles.container_Header}>
      <div>
        <Button onClick={handleOpenCreateModal} icon={<PlusIcon size='xs' />} iconPosition='left'>
          新規作成
        </Button>
      </div>
    </div>

    <div className={styles.table}>
      <Table
        isFixed
        headers={[
          {
            label: '並び順',
            className: styles.colSortOrder,
            helpContent: {
              content: 'ドラッグ操作で並び順を変更できます',
              showHelp: showTooltip,
              hideHelp: hideTooltip,
              helpId: tooltipId,
            },
          },
          { label: 'アイコン', className: styles.colIcon },
          { label: '表示名' },
          { label: 'リンク先' },
          { label: 'ファイル' },
          { label: '設定', className: styles.colAction },
        ]}
      >
        <SortableList
          variant='table'
          items={portalItems}
          onReorder={onReOrder}
          renderItem={(row, dragHandleProps) => (
            <>
              <TableCell>
                <div
                  className={styles.dragHandle}
                  {...dragHandleProps.attributes}
                  {...dragHandleProps.listeners}
                >
                  <GripVerticalIcon size='xs' />
                </div>
              </TableCell>
              <TableCell>
                <PortalIcon iconName={row.iconName} />
              </TableCell>
              <TableCell className={styles.truncatedCell}>{row.title}</TableCell>
              <TableCell className={styles.truncatedCell}>{row.linkUrl}</TableCell>
              <TableCell className={styles.truncatedCell}>{row.fileUrl}</TableCell>
              <TableCell align='center'>
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <button
                      type='button'
                      className={iconButtonVariants({ variant: 'ghost', size: 'sm' })}
                      aria-label='メニューを開く'
                      onClick={e => e.stopPropagation()}
                    >
                      <EllipsisVerticalIcon />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end' onClick={e => e.stopPropagation()}>
                    <DropdownMenuItem onSelect={() => handleEditPortal(row)}>編集</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className={styles.deleteItem}
                      onSelect={() => handleDeletePortal(row)}
                    >
                      削除
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </>
          )}
        />
      </Table>
    </div>
  </div>
);
